import { eachDayOfInterval, format, subMilliseconds } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import sortBy from 'lodash.sortby';

import {
  getLocalisedDate,
  ItemWithTimeStatus,
  TimeStatus,
} from '../../../helpers';

export const ISO_DATE_FORMAT = 'yyyy-MM-dd';

const searchFilter = (item: ItemWithTimeStatus, searchFilter: string) =>
  searchFilter.length === 0 ||
  item.name.toLowerCase().includes(searchFilter) ||
  item.description?.toLowerCase().includes(searchFilter) ||
  item.speakers
    .map(s => s.name.toLowerCase())
    .join(',')
    .includes(searchFilter) ||
  item.tags
    .map(tag => tag.name.toLowerCase())
    .join(',')
    .includes(searchFilter);

const partFilter = (item: ItemWithTimeStatus, selectedParts: string[]) =>
  selectedParts.length === 0 ||
  selectedParts.some(part => item.event_part_type === part);

const tagFilter = (item: ItemWithTimeStatus, selectedTags: string[]) =>
  selectedTags.length === 0 ||
  item.tags.some(tag => selectedTags.includes(tag.name));

const timeStatusFilter = (
  item: ItemWithTimeStatus,
  selectedTimeStatuses: TimeStatus[],
) =>
  selectedTimeStatuses.length === 0 ||
  !item.timeStatus ||
  selectedTimeStatuses.some(status => item.timeStatus === status);

const speakerFilter = (item: ItemWithTimeStatus, selectedSpeakers: string[]) =>
  selectedSpeakers.length === 0 ||
  item.speakers.some(speaker => selectedSpeakers.includes(speaker.name));

const eventTypeFilter = (
  item: ItemWithTimeStatus,
  selectedEventTypes: string[],
) =>
  selectedEventTypes.length === 0 ||
  selectedEventTypes.includes(item.event_type);

const editingStatusFilter = (
  item: ItemWithTimeStatus,
  selectedEditingStatuses: string[],
) =>
  selectedEditingStatuses.length === 0 ||
  selectedEditingStatuses.includes(item.draft ? 'draft' : 'final');

interface FilteredItemsArgs {
  items: ItemWithTimeStatus[];
  search?: string;
  selectedTags?: string[];
  selectedSpeakers?: string[];
  selectedParts?: string[];
  selectedEventTypes?: string[];
  selectedTimeStatuses?: TimeStatus[];
  selectedEditingStatuses?: string[];
}

const sortItems = (items: ItemWithTimeStatus[]) =>
  sortBy<ItemWithTimeStatus>(items, 'time_start', 'time_end');

export const getFilteredItems = ({
  items = [],
  search = '',
  selectedTags = [],
  selectedSpeakers = [],
  selectedParts = [],
  selectedEventTypes = [],
  selectedTimeStatuses = [],
  selectedEditingStatuses = [],
}: FilteredItemsArgs) => {
  return sortItems(
    items
      .filter(item => searchFilter(item, search.toLowerCase()))
      .filter(item => partFilter(item, selectedParts))
      .filter(item => tagFilter(item, selectedTags))
      .filter(item => eventTypeFilter(item, selectedEventTypes))
      .filter(item => speakerFilter(item, selectedSpeakers))
      .filter(item => timeStatusFilter(item, selectedTimeStatuses))
      .filter(item => editingStatusFilter(item, selectedEditingStatuses)),
  );
};

interface ItemsByDayArgs {
  start?: Date;
  end?: Date;
  timezone?: string;
  items: ItemWithTimeStatus[];
  filteredItems: ItemWithTimeStatus[];
}

export const getItemsByDay = ({
  items,
  filteredItems,
  start,
  end,
  timezone,
}: ItemsByDayArgs) => {
  // eachDayOfInterval is inclusive, so 00:00 on the last day would show a tab for an empty day.
  // Removing a millisecond makes it exclusive

  const eventDays =
    start && end && eachDayOfInterval({ start, end: subMilliseconds(end, 1) });

  const itemsDays =
    !eventDays &&
    Array.from(
      new Set(sortItems(items).map(item => getLocalisedDate(item.time_start))),
    );

  const days = eventDays || itemsDays || [];

  const buckets = days.reduce<{
    [key: string]: any[];
  }>(
    (prev, curr) => ({
      ...prev,
      [format(curr, ISO_DATE_FORMAT)]: [],
    }),
    {},
  );

  filteredItems.forEach(item => {
    const day = format(
      timezone
        ? utcToZonedTime(item.time_start, timezone)
        : getLocalisedDate(item.time_start),
      ISO_DATE_FORMAT,
    );
    if (buckets[day]) {
      buckets[day].push(item);
    } else {
      throw new Error(
        `Item ${item.time_start} exists outside of event boundaries - ${start} - ${end}, ${timezone}`,
      );
    }
  });

  return Object.entries(buckets);
};
